// Client-side image resizing/compression used before uploading to Supabase Storage.
// Images are re-encoded as WebP (JPEG where the browser can't encode WebP, e.g. older Safari)
// and downscaled by repeated halving, which keeps detail sharp without aliasing.

// Quality per output width. Smaller variants are viewed smaller, so they tolerate a bit more compression.
const QUALITY_BY_WIDTH = { 1920: 0.85, 1600: 0.85, 800: 0.83, 400: 0.82 };
const DEFAULT_QUALITY = 0.85;
// Keep the first decode under iOS Safari's canvas limit (~16.7M pixels)
const MAX_WORKING_PIXELS = 12_000_000;

let webpSupportPromise = null;
const supportsWebpEncoding = () => {
  if (!webpSupportPromise) {
    webpSupportPromise = new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      canvas.width = canvas.height = 1;
      canvas.toBlob((blob) => resolve(!!blob && blob.type === 'image/webp'), 'image/webp', 0.8);
    });
  }
  return webpSupportPromise;
};

const loadImage = (file) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => {
    URL.revokeObjectURL(url);
    resolve(img);
  };
  img.onerror = () => {
    URL.revokeObjectURL(url);
    reject(new Error(`Could not read image "${file.name}"`));
  };
  img.src = url;
});

const drawScaled = (source, width, height, background) => {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (background) {
    ctx.fillStyle = background;
    ctx.fillRect(0, 0, width, height);
  }
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(source, 0, 0, width, height);
  return canvas;
};

const canvasToBlob = (canvas, type, quality) => new Promise((resolve, reject) => {
  canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Image encoding failed'))), type, quality);
});

/**
 * Resize an image file to each requested width (never upscaling) and compress it.
 * Returns [{ width, blob, type, ext }] in the same order as `widths`.
 */
export const createImageVariants = async (file, widths) => {
  const img = await loadImage(file);
  const useWebp = await supportsWebpEncoding();
  const type = useWebp ? 'image/webp' : 'image/jpeg';
  const ext = useWebp ? 'webp' : 'jpg';
  // JPEG has no transparency, so flatten onto white like the product cards
  const background = useWebp ? null : '#FFFFFF';

  const srcW = img.naturalWidth;
  const srcH = img.naturalHeight;
  const largest = Math.max(...widths);

  // First pass straight from the decoded image to a bounded working canvas
  const pixelScale = Math.sqrt(MAX_WORKING_PIXELS / (srcW * srcH));
  let workW = Math.min(srcW, largest * 2, Math.floor(srcW * pixelScale));
  let working = drawScaled(img, workW, Math.round((srcH * workW) / srcW), background);

  const results = new Map();
  // Walk from the largest width down, halving each step so every downscale is at most 2x
  for (const width of [...new Set(widths)].sort((a, b) => b - a)) {
    const targetW = Math.min(width, srcW);
    while (workW / 2 >= targetW) {
      workW = Math.round(workW / 2);
      working = drawScaled(working, workW, Math.round((srcH * workW) / srcW));
    }
    const output = workW === targetW
      ? working
      : drawScaled(working, targetW, Math.round((srcH * targetW) / srcW));
    const blob = await canvasToBlob(output, type, QUALITY_BY_WIDTH[width] || DEFAULT_QUALITY);
    results.set(width, { width, blob, type, ext });
  }

  return widths.map((width) => results.get(width));
};
